import React, { useEffect, useMemo, useState } from 'react';

import {
    CompareDealsInput,
    CompareDealsOutput,
    ContractMappingDifference,
    ContractMappingGroup,
    EntitiesList,
    Entity,
    EntityInfo
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import infoTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { isEmpty } from 'lodash';
import DatePicker from 'react-datepicker';
import { Link } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { LINK_COLOR } from 'src/constants/agGrid';
import { CM_UI_SUPPORT_SERVICE_ID_LIST } from 'src/constants/context';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { useBatchSearchParams } from 'src/hooks/useBatchSearchParams/useBatchSearchParams';
import { useSearchParam } from 'src/hooks/useSearchParam/useSearchParam';
import 'src/styles/react-datepicker.css';

import { dateToString, stringToDate } from '../MonthRangePicker/utils';
import './CompareDeals.scss';

// Function to get previous months in YYYY-MM format
const getPreviousMonths = (count = 2): string[] => {
    const months: string[] = [];
    const today = new Date();

    // Start with previous month (deals are usually created at the end of month)
    let currentMonth = today.getMonth();
    let currentYear = today.getFullYear();

    // Go back one month to start
    currentMonth--;
    if (currentMonth < 0) {
        currentMonth = 11; // December
        currentYear--;
    }

    for (let i = 0; i < count; i++) {
        // Format month with leading zero if needed
        const monthStr = (currentMonth + 1).toString().padStart(2, '0');
        months.push(`${currentYear}-${monthStr}`);

        // Go back one more month
        currentMonth--;
        if (currentMonth < 0) {
            currentMonth = 11; // December
            currentYear--;
        }
    }

    return months;
};

interface IServiceInfo {
    serviceId: string;
    serviceName: string;
}

type ContractMappingDifferenceWithService = ContractMappingDifference & IServiceInfo & { entityId: string };
type ContractMappingGroupWithService = ContractMappingGroup & IServiceInfo;
type EntityInfoWithService = EntityInfo & IServiceInfo;

// Define the extended output interface
interface ICompareDealsOutputWithService {
    differentMappings: { [key: string]: ContractMappingDifferenceWithService };
    contractMappingGroups: { [key: string]: ContractMappingGroupWithService };
    entitiesOnlyInMonth1: EntityInfoWithService[];
    entitiesOnlyInMonth2: EntityInfoWithService[];
}

export const CompareDeals: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<Record<string, CompareDealsOutput>>({});
    const [activeTab, setActiveTab] = useState<string>('groupByVendor');
    const { setTopAlertMsg, metadata } = React.useContext(AppContext);
    const services = metadata.serviceMapping;
    const { setMultipleParams } = useBatchSearchParams();

    const [month1, setMonth1] = useSearchParam('month1');
    const [month2, setMonth2] = useSearchParam('month2');

    // Local state for date pickers
    const [month1Date, setMonth1Date] = useState<Date | null>(null);
    const [month2Date, setMonth2Date] = useState<Date | null>(null);

    // Pre-fill month inputs with recent months on initial load
    useEffect(() => {
        // Only pre-fill if both inputs are empty (initial load)
        if (!month1 && !month2) {
            const [prevMonth, prevPrevMonth] = getPreviousMonths(2);

            // Set both parameters at once using our batch hook
            setMultipleParams(
                {
                    month1: prevPrevMonth,
                    month2: prevMonth
                },
                true
            );
        }
    }, []);

    // Sync date picker state with URL params
    useEffect(() => {
        if (month1) {
            setMonth1Date(stringToDate(month1));
        }
        if (month2) {
            setMonth2Date(stringToDate(month2));
        }
    }, [month1, month2]);

    // Handle month 1 change
    const handleMonth1Change = (date: Date | null) => {
        setMonth1Date(date);
        if (date) {
            setMonth1(dateToString(date));
        }
    };

    // Handle month 2 change
    const handleMonth2Change = (date: Date | null) => {
        setMonth2Date(date);
        if (date) {
            setMonth2(dateToString(date));
        }
    };

    // Check if inputs are valid and complete
    const inputsAreValid = month1 && month2;

    // Fetch data for all services when inputs are valid
    useEffect(() => {
        if (!inputsAreValid) {
            return;
        }

        setLoading(true);

        // Create promises for all services
        const promiseList = CM_UI_SUPPORT_SERVICE_ID_LIST.map((serviceId) => {
            return contractConfigurationClient
                .compareDeals(
                    CompareDealsInput.clone({
                        month1: month1.replace('-', ''),
                        month2: month2.replace('-', ''),
                        serviceId
                    })
                )
                .toPromise();
        });

        Promise.all(promiseList)
            .then((responseList) => {
                // Create a map of serviceId -> response
                const newData = {} as Record<string, CompareDealsOutput>;
                responseList.forEach((response, index) => {
                    newData[CM_UI_SUPPORT_SERVICE_ID_LIST[index].toString()] = response;
                });

                setData(newData);
                setLoading(false);
            })
            .catch((error: UnknownCoralError) => {
                // eslint-disable-next-line no-console
                const errorMessage =
                    error.receivedException?.message || 'An unknown error occurred while fetching compare deals data';

                setTopAlertMsg(errorMessage);
                setLoading(false);
            });
    }, [month1, month2]);

    const processEntityList = (
        sourceList: EntitiesList | undefined,
        targetList: EntityInfoWithService[],
        serviceId: string
    ) => {
        if (sourceList) {
            sourceList.forEach((entity) => {
                // Filter out empty objects
                if (entity.entityId && entity.entityName) {
                    targetList.push({
                        ...entity,
                        serviceId,
                        serviceName: services[parseInt(serviceId)].serviceName ?? ''
                    } as EntityInfoWithService);
                }
            });
        }
    };

    // Combine data from all services
    const combinedData = useMemo(() => {
        if (isEmpty(data)) return null;

        const result: ICompareDealsOutputWithService = {
            differentMappings: {},
            contractMappingGroups: {},
            entitiesOnlyInMonth1: [],
            entitiesOnlyInMonth2: []
        };

        // Merge data from all services
        Object.keys(data).forEach((serviceId) => {
            const serviceData = data[serviceId];
            // Merge differentMappings
            if (serviceData.differentMappings) {
                Object.entries(serviceData.differentMappings).forEach(([entityId, value]) => {
                    // Create a unique key that includes the service ID
                    const serviceKey = `${serviceId}-${entityId}`;
                    result.differentMappings[serviceKey] = {
                        ...value,
                        entityId,
                        // Add service information
                        serviceId: serviceId,
                        serviceName: services[parseInt(serviceId)].serviceName ?? ''
                    } as ContractMappingDifferenceWithService;
                });
            }

            // Merge contractMappingGroups
            if (serviceData.contractMappingGroups) {
                Object.entries(serviceData.contractMappingGroups).forEach(([key, value]) => {
                    // Create a unique key that includes the service ID
                    const serviceKey = `${serviceId}-${key}`;
                    result.contractMappingGroups[serviceKey] = {
                        ...value,
                        // Add service information
                        serviceId: serviceId,
                        serviceName: services[parseInt(serviceId)].serviceName ?? ''
                    } as ContractMappingGroupWithService;
                });
            }

            // Merge entitiesOnlyInMonth1
            processEntityList(serviceData.entitiesOnlyInMonth1, result.entitiesOnlyInMonth1, serviceId);

            // Merge entitiesOnlyInMonth2
            processEntityList(serviceData.entitiesOnlyInMonth2, result.entitiesOnlyInMonth2, serviceId);
        });

        return result;
    }, [data, services]);

    const centerCellStyle = {
        display: 'flex',
        alignItems: 'center'
    };

    // Add service column to all column definitions
    const serviceColumn = {
        headerName: 'Service',
        field: 'serviceName',
        sortable: true,
        filter: true,
        width: 100,
        cellStyle: centerCellStyle
    };

    const groupByVendorGridColumnDefs: ColDef[] = useMemo(
        () => [
            {
                headerName: 'Vendor Name',
                field: 'entityName',
                sortable: true,
                filter: true,
                width: 200,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <Link
                            style={{ color: LINK_COLOR, textDecoration: 'none' }}
                            to={`${ROUTES.VENDORS.INDEX}/${params.data.entityId}`}
                            target="_blank"
                        >
                            {params.value}
                        </Link>
                    );
                }
            },
            {
                headerName: 'VendorID',
                field: 'entityId',
                sortable: true,
                filter: true,
                width: 100
            },
            serviceColumn,
            {
                headerName: `${month1 ?? 'Month 1'} Contract`,
                field: 'month1ContractName',
                sortable: true,
                filter: true,
                width: 200,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <Link
                            style={{ color: LINK_COLOR, textDecoration: 'none' }}
                            to={`${ROUTES.RATE_CARDS.INDEX}/${params.data.month1ContractId}`}
                            target="_blank"
                        >
                            {params.value}
                        </Link>
                    );
                }
            },
            {
                headerName: `${month1 ?? 'Month 1'} ContractID`,
                field: 'month1ContractId',
                sortable: true,
                filter: true,
                width: 120
            },
            {
                headerName: `${month2 ?? 'Month 2'} Contract`,
                field: 'month2ContractName',
                sortable: true,
                filter: true,
                width: 200,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <Link
                            style={{ color: LINK_COLOR, textDecoration: 'none' }}
                            to={`${ROUTES.RATE_CARDS.INDEX}/${params.data.month2ContractId}`}
                            target="_blank"
                        >
                            {params.value}
                        </Link>
                    );
                }
            },
            {
                headerName: `${month2 ?? 'Month 2'} ContractID`,
                field: 'month2ContractId',
                sortable: true,
                filter: true,
                width: 120
            },
            {
                headerName: 'Compare Contracts',
                width: 150,
                cellStyle: centerCellStyle,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <Link
                            style={{ color: LINK_COLOR, textDecoration: 'none' }}
                            to={`${ROUTES.COMPARE.CONTRACTS}?leftId=${params.data.month1ContractId}&rightId=${params.data.month2ContractId}`}
                            target="_blank"
                        >
                            Link
                        </Link>
                    );
                }
            }
        ],
        [month1, month2]
    );

    const groupByContractGridColumnDefs: ColDef[] = useMemo(
        () => [
            {
                headerName: 'Vendors',
                field: 'entities',
                sortable: true,
                filter: false,
                width: 200,
                autoHeight: true,
                cellRenderer: (params: ICellRendererParams) => {
                    const entityList = params.value;
                    return (
                        <ul>
                            {entityList.map((entity: Entity) => {
                                return (
                                    <li key={entity.entityId}>
                                        <Link
                                            style={{ color: LINK_COLOR, textDecoration: 'none' }}
                                            key={entity.entityId}
                                            to={`${ROUTES.VENDORS.INDEX}/${entity.entityId}`}
                                            target="_blank"
                                        >
                                            {entity.entityName}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    );
                }
            },
            serviceColumn,
            {
                headerName: `${month1 ?? 'Month 1'} ContractID`,
                field: 'month1ContractId',
                sortable: true,
                filter: true,
                width: 120,
                cellStyle: centerCellStyle
            },
            {
                headerName: `${month1 ?? 'Month 1'} Contract`,
                field: 'month1ContractName',
                sortable: true,
                filter: true,
                width: 200,
                cellStyle: centerCellStyle,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <Link
                            style={{ color: LINK_COLOR, textDecoration: 'none' }}
                            to={`${ROUTES.RATE_CARDS.INDEX}/${params.data.month1ContractId}`}
                            target="_blank"
                        >
                            {params.value}
                        </Link>
                    );
                }
            },
            {
                headerName: `${month2 ?? 'Month 2'} ContractID`,
                field: 'month2ContractId',
                sortable: true,
                filter: true,
                width: 120,
                cellStyle: centerCellStyle
            },
            {
                headerName: `${month2 ?? 'Month 2'} Contract`,
                field: 'month2ContractName',
                sortable: true,
                filter: true,
                width: 200,
                cellStyle: centerCellStyle,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <Link
                            style={{ color: LINK_COLOR, textDecoration: 'none' }}
                            to={`${ROUTES.RATE_CARDS.INDEX}/${params.data.month2ContractId}`}
                            target="_blank"
                        >
                            {params.value}
                        </Link>
                    );
                }
            },
            {
                headerName: 'Compare Contracts',
                width: 150,
                cellStyle: centerCellStyle,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <Link
                            style={{ color: LINK_COLOR, textDecoration: 'none' }}
                            to={`${ROUTES.COMPARE.CONTRACTS}?leftId=${params.data.month1ContractId}&rightId=${params.data.month2ContractId}`}
                            target="_blank"
                        >
                            Link
                        </Link>
                    );
                }
            }
        ],
        [month1, month2]
    );

    const entitiesOnlyColumnDefs: ColDef[] = [
        {
            headerName: 'Vendor Name',
            field: 'entityName',
            sortable: true,
            filter: true,
            width: 350,
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    <Link
                        style={{ color: LINK_COLOR, textDecoration: 'none' }}
                        to={`${ROUTES.VENDORS.INDEX}/${params.data.entityId}`}
                        target="_blank"
                    >
                        {params.value}
                    </Link>
                );
            }
        },
        {
            headerName: 'VendorID',
            field: 'entityId',
            sortable: true,
            filter: true,
            width: 100
        },
        serviceColumn
    ];

    const groupByContractGridData = combinedData?.contractMappingGroups
        ? Object.entries(combinedData.contractMappingGroups).map(([key, value]) => {
              return {
                  month1ContractId: value.month1ContractId,
                  month1ContractName: value.month1ContractName,
                  month2ContractId: value.month2ContractId,
                  month2ContractName: value.month2ContractName,
                  entities: value.entities,
                  serviceId: value.serviceId,
                  serviceName: value.serviceName
              };
          })
        : [];

    const groupByVendorGridData = combinedData?.differentMappings
        ? Object.entries(combinedData.differentMappings).map(([key, value]) => ({
              entityId: value.entityId,
              entityName: value.entityName,
              month1ContractId: value.month1ContractId,
              month1ContractName: value.month1ContractName,
              month2ContractId: value.month2ContractId,
              month2ContractName: value.month2ContractName,
              serviceId: value.serviceId,
              serviceName: value.serviceName
          }))
        : [];

    return (
        <Column heights={['fit', 'fit', 'fill']} width={'100%'}>
            <Column>
                <Heading level={2}>Compare Deals</Heading>
                <Text type="b200">
                    This page helps identify deal changes between monthly snapshots and track which vendors have been
                    mapped to different contracts or newly onboarded.
                </Text>
            </Column>
            <Box type="fill" spacingInset="400">
                <Column spacing="400">
                    <Heading level={6}>Select two months to compare deal snapshots.</Heading>
                    <Row widths={['grid-3', 'grid-3']} alignmentVertical="top">
                        <Row spacing="200">
                            <Text>Month 1</Text>

                            <DatePicker
                                className="month-filter-input"
                                selected={month1Date}
                                onChange={handleMonth1Change}
                                dateFormat="yyyy-MM"
                                showMonthYearPicker
                                placeholderText="Select Month 1"
                            />
                        </Row>
                        <Row spacing="200">
                            <Text>Month 2</Text>

                            <DatePicker
                                className="month-filter-input"
                                selected={month2Date}
                                onChange={handleMonth2Change}
                                dateFormat="yyyy-MM"
                                showMonthYearPicker
                                placeholderText="Select Month 2"
                            />
                        </Row>
                    </Row>
                </Column>
            </Box>

            <Column width="100%" heights={['fit', 'fill']} spacing="400">
                <Column height="100%" width="100%">
                    <TabGroup value={activeTab} onChange={setActiveTab} fill="line">
                        <Tab value="groupByVendor">
                            Group by Vendor{' '}
                            <Tooltip
                                title="
                                       This tab shows vendors whose contract mappings changed between selected months. Each row represents a vendor with different contract mapping.
                                    "
                                position="end"
                            >
                                <Icon tokens={infoTokens} />
                            </Tooltip>
                        </Tab>
                        <Tab value="groupByContract">
                            Group by Contract{' '}
                            <Tooltip
                                title="This tab groups vendors by their contract mappings, showing which vendors share the same contract changes between the selected months."
                                position="end"
                            >
                                <Icon tokens={infoTokens} />
                            </Tooltip>
                        </Tab>
                        <Tab value="entitiesOnlyInMonth1">
                            Vendors onboarded new deals in {month1}{' '}
                            <Tooltip
                                title="This tab shows vendors that have deals in the first selected month only."
                                position="end"
                            >
                                <Icon tokens={infoTokens} />
                            </Tooltip>
                        </Tab>
                        <Tab value="entitiesOnlyInMonth2">
                            Vendors onboarded new deals in {month2}{' '}
                            <Tooltip
                                title="This tab shows vendors that have deals in in the second selected month only."
                                position="end"
                            >
                                <Icon tokens={infoTokens} />
                            </Tooltip>
                        </Tab>
                    </TabGroup>

                    {activeTab === 'groupByVendor' &&
                        (!loading && isEmpty(groupByVendorGridData) ? (
                            <Text>
                                No comparison data available. This could be because there are no deal changes between
                                the selected months.
                            </Text>
                        ) : (
                            <BaseAgGrid
                                columnDefs={groupByVendorGridColumnDefs}
                                rowData={groupByVendorGridData}
                                loading={loading}
                            />
                        ))}

                    {activeTab === 'groupByContract' &&
                        (!loading && isEmpty(groupByContractGridData) ? (
                            <Text>
                                No comparison data available. This could be because there are no deal changes between
                                the selected months.
                            </Text>
                        ) : (
                            <BaseAgGrid
                                columnDefs={groupByContractGridColumnDefs}
                                rowData={groupByContractGridData}
                                loading={loading}
                            />
                        ))}

                    {activeTab === 'entitiesOnlyInMonth1' &&
                        (!loading && isEmpty(combinedData?.entitiesOnlyInMonth1) ? (
                            <Text>No new deals created in {month1}.</Text>
                        ) : (
                            <BaseAgGrid
                                columnDefs={entitiesOnlyColumnDefs}
                                rowData={combinedData?.entitiesOnlyInMonth1 ?? []}
                                loading={loading}
                            />
                        ))}

                    {activeTab === 'entitiesOnlyInMonth2' &&
                        (!loading && isEmpty(combinedData?.entitiesOnlyInMonth2) ? (
                            <Text>No new deals created in {month2}.</Text>
                        ) : (
                            <BaseAgGrid
                                columnDefs={entitiesOnlyColumnDefs}
                                rowData={combinedData?.entitiesOnlyInMonth2 ?? []}
                                loading={loading}
                            />
                        ))}
                </Column>
            </Column>
        </Column>
    );
};
